import { createSlice } from "@reduxjs/toolkit";
import {
  // Pricing Rule thunks
  createPricingRuleThunk,
  getPricingRulesThunk,
  getPricingRuleByIdThunk,
  updatePricingRuleThunk,
  deletePricingRuleThunk,
  clonePricingRuleThunk,
  togglePricingRuleStatusThunk, // ✅ ADDED

  // Calculation thunks
  calculatePricingThunk,
  calculateBulkPricingThunk,
  calculatePriceWithOffersThunk,

  // Actions
  clearPricingError,
  clearPricingCalculation,
  clearBulkCalculation,
  resetPricingState,
  setCurrentRule,
  clearCurrentRule,
  getPricingRuleAnalyticsThunk,
  getPricingRuleAuditLogThunk,
} from "../thunks/pricingThunks";

const initialState = {
  // Pricing Rules
  pricingRules: [],
  currentPricingRule: null,
  pricingRulesLoading: false,
  pricingRulesSaving: false,
  pricingRulesError: null,

  // Calculations
  calculatedPricing: null,
  bulkPricingResults: [],
  calculationLoading: false,
  calculationError: null,

  // UI State
  selectedRuleIds: [],
  filters: {
    active: null,
    productType: null,
    branchCode: null,
  },
  sortBy: "priority",
  sortOrder: "desc",

  // Pagination
  page: 1,
  limit: 20,
  totalCount: 0,
  totalPages: 0,

  // ✅ NEW: Analytics
  ruleAnalytics: null,
  analyticsLoading: false,
  analyticsError: null,

  // ✅ NEW: Audit Log
  ruleAuditLog: [],
  auditLogPagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },
  auditLogLoading: false,
  auditLogError: null,
};

const pricingSlice = createSlice({
  name: "pricing",
  initialState,
  reducers: {
    // Rule Selection
    setSelectedRuleIds: (state, action) => {
      state.selectedRuleIds = action.payload;
    },
    toggleRuleSelection: (state, action) => {
      const id = action.payload;
      const index = state.selectedRuleIds.indexOf(id);
      if (index > -1) {
        state.selectedRuleIds.splice(index, 1);
      } else {
        state.selectedRuleIds.push(id);
      }
    },
    selectAllRules: (state) => {
      state.selectedRuleIds = state.pricingRules.map((rule) => rule._id);
    },
    deselectAllRules: (state) => {
      state.selectedRuleIds = [];
    },

    // Filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1; // Reset page when filters change
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.page = 1;
    },

    // Sorting
    setSortBy: (state, action) => {
      if (typeof action.payload === "string") {
        state.sortBy = action.payload;
      } else {
        state.sortBy = action.payload.sortBy || state.sortBy;
        state.sortOrder = action.payload.sortOrder || state.sortOrder;
      }
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
      state.page = 1; // Reset page when limit changes
    },
    // ✅ NEW: Clear analytics
    clearAnalytics: (state) => {
      state.ruleAnalytics = null;
      state.analyticsError = null;
    },

    // ✅ NEW: Clear audit log
    clearAuditLog: (state) => {
      state.ruleAuditLog = [];
      state.auditLogError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GENERAL ACTIONS =====
      .addCase(clearPricingError, (state) => {
        state.pricingRulesError = null;
        state.calculationError = null;
      })
      .addCase(clearPricingCalculation, (state) => {
        state.calculatedPricing = null;
        state.calculationError = null;
      })
      .addCase(clearBulkCalculation, (state) => {
        state.bulkPricingResults = [];
        state.calculationError = null;
      })
      .addCase(resetPricingState, () => initialState)
      .addCase(setCurrentRule, (state, action) => {
        state.currentPricingRule = action.payload;
      })
      .addCase(clearCurrentRule, (state) => {
        state.currentPricingRule = null;
      })

      // ===== PRICING RULES =====

      // Create Pricing Rule
      .addCase(createPricingRuleThunk.pending, (state) => {
        state.pricingRulesSaving = true;
        state.pricingRulesError = null;
      })
      .addCase(createPricingRuleThunk.fulfilled, (state, action) => {
        state.pricingRulesSaving = false;
        const newRule = action.payload.data || action.payload;
        state.pricingRules.unshift(newRule);
        state.totalCount += 1;
      })
      .addCase(createPricingRuleThunk.rejected, (state, action) => {
        state.pricingRulesSaving = false;
        state.pricingRulesError = action.payload;
      })

      // Get Pricing Rules
      .addCase(getPricingRulesThunk.pending, (state) => {
        state.pricingRulesLoading = true;
        state.pricingRulesError = null;
      })
      .addCase(getPricingRulesThunk.fulfilled, (state, action) => {
        state.pricingRulesLoading = false;
        const payload = action.payload;
        state.pricingRules = payload.data || payload.rules || [];
        state.totalCount =
          payload.pagination?.total || state.pricingRules.length;
        state.totalPages =
          payload.pagination?.totalPages ||
          Math.ceil(state.totalCount / state.limit);
        state.page = payload.pagination?.page || state.page;
      })
      .addCase(getPricingRulesThunk.rejected, (state, action) => {
        state.pricingRulesLoading = false;
        state.pricingRulesError = action.payload;
      })

      // Get Pricing Rule by ID
      .addCase(getPricingRuleByIdThunk.pending, (state) => {
        state.pricingRulesLoading = true;
        state.pricingRulesError = null;
      })
      .addCase(getPricingRuleByIdThunk.fulfilled, (state, action) => {
        state.pricingRulesLoading = false;
        state.currentPricingRule = action.payload.data || action.payload;
      })
      .addCase(getPricingRuleByIdThunk.rejected, (state, action) => {
        state.pricingRulesLoading = false;
        state.pricingRulesError = action.payload;
      })

      // Update Pricing Rule
      .addCase(updatePricingRuleThunk.pending, (state) => {
        state.pricingRulesSaving = true;
        state.pricingRulesError = null;
      })
      .addCase(updatePricingRuleThunk.fulfilled, (state, action) => {
        state.pricingRulesSaving = false;
        const updatedRule = action.payload.data || action.payload;
        const index = state.pricingRules.findIndex(
          (rule) => rule._id === updatedRule._id
        );
        if (index !== -1) {
          state.pricingRules[index] = updatedRule;
        }
        if (state.currentPricingRule?._id === updatedRule._id) {
          state.currentPricingRule = updatedRule;
        }
      })
      .addCase(updatePricingRuleThunk.rejected, (state, action) => {
        state.pricingRulesSaving = false;
        state.pricingRulesError = action.payload;
      })

      // Delete Pricing Rule
      .addCase(deletePricingRuleThunk.pending, (state) => {
        state.pricingRulesLoading = true;
        state.pricingRulesError = null;
      })
      .addCase(deletePricingRuleThunk.fulfilled, (state, action) => {
        state.pricingRulesLoading = false;
        const deletedId =
          action.payload.deletedId ||
          action.payload.deletedRule?.id ||
          action.meta.arg;
        state.pricingRules = state.pricingRules.filter(
          (rule) => rule._id !== deletedId
        );
        state.totalCount = Math.max(0, state.totalCount - 1);
        if (state.currentPricingRule?._id === deletedId) {
          state.currentPricingRule = null;
        }
      })
      .addCase(deletePricingRuleThunk.rejected, (state, action) => {
        state.pricingRulesLoading = false;
        state.pricingRulesError = action.payload;
      })

      // Clone Pricing Rule
      .addCase(clonePricingRuleThunk.pending, (state) => {
        state.pricingRulesSaving = true;
        state.pricingRulesError = null;
      })
      .addCase(clonePricingRuleThunk.fulfilled, (state, action) => {
        state.pricingRulesSaving = false;
        const clonedRule = action.payload.data || action.payload;
        state.pricingRules.unshift(clonedRule);
        state.totalCount += 1;
      })
      .addCase(clonePricingRuleThunk.rejected, (state, action) => {
        state.pricingRulesSaving = false;
        state.pricingRulesError = action.payload;
      })

      // ✅ ADDED: Toggle Pricing Rule Status
      .addCase(togglePricingRuleStatusThunk.pending, (state) => {
        state.pricingRulesSaving = true;
        state.pricingRulesError = null;
      })
      .addCase(togglePricingRuleStatusThunk.fulfilled, (state, action) => {
        state.pricingRulesSaving = false;
        const updatedRule = action.payload.data || action.payload;
        const index = state.pricingRules.findIndex(
          (rule) => rule._id === updatedRule._id
        );
        if (index !== -1) {
          state.pricingRules[index] = updatedRule;
        }
        if (state.currentPricingRule?._id === updatedRule._id) {
          state.currentPricingRule = updatedRule;
        }
      })
      .addCase(togglePricingRuleStatusThunk.rejected, (state, action) => {
        state.pricingRulesSaving = false;
        state.pricingRulesError = action.payload;
      })

      // ===== CALCULATIONS =====

      // Calculate Pricing
      .addCase(calculatePricingThunk.pending, (state) => {
        state.calculationLoading = true;
        state.calculationError = null;
      })
      .addCase(calculatePricingThunk.fulfilled, (state, action) => {
        state.calculationLoading = false;
        state.calculatedPricing = action.payload.data || action.payload;
      })
      .addCase(calculatePricingThunk.rejected, (state, action) => {
        state.calculationLoading = false;
        state.calculationError = action.payload;
      })

      // Calculate Bulk Pricing
      .addCase(calculateBulkPricingThunk.pending, (state) => {
        state.calculationLoading = true;
        state.calculationError = null;
      })
      .addCase(calculateBulkPricingThunk.fulfilled, (state, action) => {
        state.calculationLoading = false;
        state.bulkPricingResults = action.payload.data || action.payload;
      })
      .addCase(calculateBulkPricingThunk.rejected, (state, action) => {
        state.calculationLoading = false;
        state.calculationError = action.payload;
      })

      // Calculate Price with Offers
      .addCase(calculatePriceWithOffersThunk.pending, (state) => {
        state.calculationLoading = true;
        state.calculationError = null;
      })
      .addCase(calculatePriceWithOffersThunk.fulfilled, (state, action) => {
        state.calculationLoading = false;
        state.calculatedPricing = action.payload.data || action.payload;
      })
      .addCase(calculatePriceWithOffersThunk.rejected, (state, action) => {
        state.calculationLoading = false;
        state.calculationError = action.payload;
      })
      .addCase(getPricingRuleAnalyticsThunk.pending, (state) => {
        state.analyticsLoading = true;
        state.analyticsError = null;
      })
      .addCase(getPricingRuleAnalyticsThunk.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.ruleAnalytics = action.payload.data;
      })
      .addCase(getPricingRuleAnalyticsThunk.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsError = action.payload;
      })

      // ===== AUDIT LOG =====
      .addCase(getPricingRuleAuditLogThunk.pending, (state) => {
        state.auditLogLoading = true;
        state.auditLogError = null;
      })
      .addCase(getPricingRuleAuditLogThunk.fulfilled, (state, action) => {
        state.auditLogLoading = false;
        state.ruleAuditLog = action.payload.data;
        state.auditLogPagination = action.payload.pagination;
      })
      .addCase(getPricingRuleAuditLogThunk.rejected, (state, action) => {
        state.auditLogLoading = false;
        state.auditLogError = action.payload;
      });
  },
});

export const {
  setSelectedRuleIds,
  toggleRuleSelection,
  selectAllRules,
  deselectAllRules,
  setFilters,
  clearFilters,
  setSortBy,
  setPage,
  setLimit,
  clearAnalytics,
  clearAuditLog,
} = pricingSlice.actions;

// ===== SELECTORS =====

// Pricing Rules
export const selectPricingRules = (state) => state.pricing.pricingRules;
export const selectCurrentPricingRule = (state) =>
  state.pricing.currentPricingRule;
export const selectPricingRulesLoading = (state) =>
  state.pricing.pricingRulesLoading;
export const selectPricingRulesSaving = (state) =>
  state.pricing.pricingRulesSaving;
export const selectPricingRulesError = (state) =>
  state.pricing.pricingRulesError;

// Calculations
export const selectCalculatedPricing = (state) =>
  state.pricing.calculatedPricing;
export const selectBulkPricingResults = (state) =>
  state.pricing.bulkPricingResults;
export const selectCalculationLoading = (state) =>
  state.pricing.calculationLoading;
export const selectCalculationError = (state) => state.pricing.calculationError;

// UI State
export const selectSelectedRuleIds = (state) => state.pricing.selectedRuleIds;
export const selectPricingFilters = (state) => state.pricing.filters;
export const selectPricingSortBy = (state) => state.pricing.sortBy;
export const selectPricingSortOrder = (state) => state.pricing.sortOrder;

// Pagination
export const selectPricingPage = (state) => state.pricing.page;
export const selectPricingLimit = (state) => state.pricing.limit;
export const selectPricingTotalCount = (state) => state.pricing.totalCount;
export const selectPricingTotalPages = (state) => state.pricing.totalPages;

// Computed Selectors
export const selectPricingPagination = (state) => ({
  page: state.pricing.page,
  limit: state.pricing.limit,
  totalCount: state.pricing.totalCount,
  totalPages: state.pricing.totalPages,
});

export const selectRuleAnalytics = (state) => state.pricing.ruleAnalytics;
export const selectAnalyticsLoading = (state) => state.pricing.analyticsLoading;
export const selectAnalyticsError = (state) => state.pricing.analyticsError;

export const selectRuleAuditLog = (state) => state.pricing.ruleAuditLog;
export const selectAuditLogPagination = (state) =>
  state.pricing.auditLogPagination;
export const selectAuditLogLoading = (state) => state.pricing.auditLogLoading;
export const selectAuditLogError = (state) => state.pricing.auditLogError;

export default pricingSlice.reducer;
